import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { NewsItem } from "../common/types";
import { formatDate } from "../utils";
import TagsComponent from "./tags";
import ReactMarkdown from "react-markdown";

interface DetailViewProps {
  article: NewsItem;
  onBack: () => void;
}

const DetailView: React.FC<DetailViewProps> = ({ article, onBack }) => {
  const { t } = useTranslation("common");

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm"
          aria-label={t("news.backToNews")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("news.backToNews")}
        </button>
        <nav className="ml-4">
          <ol className="flex items-center space-x-1 text-sm text-gray-500">
            <li>
              <Link to="/news" className="hover:text-indigo-600">
                {t("nav.news")}
              </Link>
            </li>
            <li>
              <span className="mx-1">/</span>
            </li>
            <li>{article.title}</li>
          </ol>
        </nav>
      </div>

      <h2 className="text-2xl font-bold mb-4">{article.title}</h2>
      <p className="text-sm text-gray-500 mb-4">
        {formatDate(article.launch_date)}
      </p>

      <div className="prose prose-indigo text-primary dark:prose-invert max-w-none mb-4 text-sm">
        <ReactMarkdown>{article.description}</ReactMarkdown>
      </div>

      <div className="my-4">
        <TagsComponent tags={article.tags} />
      </div>

      <div className="divide-x divide-solid gap-4">
        <a
          href={article.launch_url}
          target="_blank"
          rel="noreferrer"
          className="text-indigo-600 inline-block pr-2 hover:text-indigo-800"
        >
          {t("news.announcement")}
        </a>
        {article.paper_pdf_url && (
          <a
            href={article.paper_pdf_url}
            className="text-indigo-600 px-2 hover:text-indigo-800"
            target="_blank"
            rel="noreferrer"
          >
            {t("news.paperPdf")}
          </a>
        )}

        {article.github_url && (
          <a
            href={article.github_url}
            className="text-indigo-600 px-2 hover:text-indigo-800"
            target="_blank"
            rel="noreferrer"
          >
            {t("news.github")}
          </a>
        )}
      </div>

      {article.authors && (
        <div className="mt-2">
          <p className="text-sm text-gray-600 mb-4">
            {t("news.authors")}: {article.authors.join(", ")}
          </p>
        </div>
      )}
    </div>
  );
};

export default DetailView;
